import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import gql from 'graphql-tag';
import { defineMessages, useIntl } from 'react-intl';
import { Panel } from 'primereact/panel';
import { Button } from 'primereact/button';
import { Field, Form, Formik } from 'formik';
import { useMutation } from '@apollo/react-hooks';
import { useHistory } from 'react-router-dom';
import { NotificationContext } from '../../components/GrowlNotifications';
import { growlActionSuccess, growlErrors } from '../../utils/NotificationUtil';
import { getFormattedDate } from '../../utils/AppUtil';
import { TextAreaComponent } from '../../components/prime-formik';

const STOP_SUBSCRIPTION_MUTATION = gql`
  mutation stopSubscriptionMutation($input: StopSubscriptionInput!) {
    stopSubscription(input: $input) {
      managingAccountId
      clientAccountId
    }
  }
`;

const i18nMessages = defineMessages({
  accountId: {
    id: 'account.id',
    defaultMessage: 'Account ID:',
  },
  expirationdate: {
    id: 'account.subscription.expiration-date',
    defaultMessage: 'Expiration date:',
  },
  whyAreYouStopping: {
    id: 'account.subscription.why-are-you-stopping',
    defaultMessage: 'Why are you stopping this NoLimit subscription?',
  },
  stopSubscription: {
    id: 'account.subscription.stop-subscription',
    defaultMessage: 'Stop subscription',
  },
  stopSubscriptionSuccess: {
    id: 'account.subscription.stop-subscription.success',
    defaultMessage: 'Successfully stopped subscription',
  },
});

const StopSubscription = ({ managingAccountId, clientAccount }) => {
  const { formatMessage, formatDate } = useIntl();
  const growl = useContext(NotificationContext);
  const history = useHistory();

  const [stopSubscriptionMutation] = useMutation(STOP_SUBSCRIPTION_MUTATION);

  const {
    id: clientAccountId,
    displayName: clientAccountDisplayName,
    subscriptionInformation: {
      currentSubscription: { endsOn },
    },
  } = clientAccount;

  const initialValues = {
    reason: '',
  };

  const validationSchema = Yup.object().shape({
    reason: Yup.string().required(),
  });

  const stopSubscription = async (values, resetForm) => {
    try {
      await stopSubscriptionMutation({
        variables: {
          input: {
            managingAccountId,
            clientAccountId,
            reason: values.reason,
          },
        },
      });

      growlActionSuccess(growl, formatMessage(i18nMessages.stopSubscriptionSuccess));
      history.goBack();
    } catch (mutationError) {
      growlErrors(growl, mutationError);
    } finally {
      resetForm();
    }
  };

  return (
    <div className="p-grid">
      <div className="p-col-12">
        <div className="brand-detailed-wrapper">
          <div className="p-sm-12 p-md-6">
            <Panel header={clientAccountDisplayName}>
              <div className="font-weight-600">
                <div className="existing-subscription-info">
                  <div>{formatMessage(i18nMessages.accountId)}</div>
                  <div>{clientAccountId}</div>
                </div>
                <div className="existing-subscription-info">
                  <div>{formatMessage(i18nMessages.expirationdate)}</div>
                  <div>{getFormattedDate(endsOn, formatDate)}</div>
                </div>
              </div>
              <br />
              <Formik
                enableReinitialize
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={(values, { resetForm }) => {
                  stopSubscription(values, resetForm);
                }}
              >
                {({ isSubmitting }) => (
                  <Form>
                    <div className="input-label font-weight-600">{formatMessage(i18nMessages.whyAreYouStopping)}</div>
                    <div>
                      <Field id="reason" name="reason" component={TextAreaComponent} />
                    </div>
                    <br />
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      label={formatMessage(i18nMessages.stopSubscription)}
                      className="p-button-danger"
                    />
                  </Form>
                )}
              </Formik>
            </Panel>
          </div>
        </div>
      </div>
    </div>
  );
};

StopSubscription.propTypes = {
  managingAccountId: PropTypes.string.isRequired,
  clientAccount: PropTypes.objectOf(PropTypes.any).isRequired,
};

export default StopSubscription;
